/**
 * Bulk promotion wire shapes for the submit and preflight path.
 *
 * Declared here rather than generated because the operations that carry them are still being built.
 * Replace each with the generated type once it exists; the first build after that surfaces any drift.
 * The enumerated values these reference are in bulkPromotionEnums.
 */

/**
 * What the operator asks for when previewing a bulk promotion. Deliberately no actor of any kind: the
 * promoter is the authenticated principal the service resolves from the connection's authorizer, and
 * that is what the promotion tracker records. An optional actor member is one required() gate away
 * from refusing a promotion over a value nothing downstream reads.
 */
export interface BulkPromoteRequest {
    componentType: string;
    componentIds: string[];
}

export interface BlockedItem {
    componentId: string;
    reason: string;
    detail: string;
    missingDependency?: string;
    namedMaster?: string;
}

export interface PrerequisiteEntry {
    componentType: string;
    componentId: string;
    componentName: string;
    classification?: string;
    level: number;
    blockedCount: number;
    actionability: string;
}


export interface BulkPreflightResult {
    promotable: string[];
    blocked: BlockedItem[];
    prerequisites: PrerequisiteEntry[];
    acceptedCount: number;
    validationMode: string;
    /**
     * Components already promoted and unchanged. Reported apart from `blocked` because they are not
     * failures. Absent on a verdict that predates the asynchronous report.
     */
    skipped?: string[];
    /**
     * PreflightSummary.fullyValidated. False means the dependency and draft checks did not read the
     * target, so this verdict verified nothing about it. Carried separately from validationMode
     * because the service emits both and the boolean is the one that cannot be misspelled.
     */
    fullyValidated?: boolean;
    /**
     * Pages of blocked detail merged so far, and how many the service said it would send. Unequal
     * means frames were lost, so the blocked table below is incomplete and must say so.
     */
    pagesReceived?: number;
    totalPages?: number;
    /**
     * PreflightSummary.blockedCount — how many are blocked in total, which is not the length of
     * `blocked` when a frame was lost. The count is the summary's; the rows are the frames'.
     */
    blockedCount?: number;
}

/**
 * The preflight verdict as it arrives over the WebSocket: PreflightSummary.java, field for field.
 *
 * Every collection is bare ids. Per-component detail would breach the Step Functions state-payload
 * cap on a batch where most components are blocked, so it travels on the report frames instead.
 */
export interface PreflightSummaryMessage {
    /** The batch. Named bulkPromotionId here, not batchId — that spelling is the submit ack's. */
    bulkPromotionId: string;
    promotableComponentIds: string[];
    blockedComponentIds: string[];
    /** Already promoted and unchanged. Not failures. */
    skippedComponentIds: string[];
    promotableCount: number;
    blockedCount: number;
    skippedCount: number;
    /** How hard the dependency check was. Values are the service's; see BulkValidationMode. */
    validationMode: string;
    fullyValidated: boolean;
    /** Derived from promotableCount == 0 by isNothingPromotable(). Recomputed rather than trusted. */
    nothingPromotable?: boolean;
}

/** One blocked component as the operator sees it. PreflightOperatorReport.BlockedComponentDetail. */
export interface BlockedComponentDetail {
    componentId: string;
    reason: string;
    missingDependency?: string;
    detail?: string;
}

/**
 * One frame of the preflight verdict. PreflightOperatorReport.java.
 *
 * Unlike every other message on this route this is posted as the message body itself, with no
 * WebSocketMessage envelope around it — no messageType, no status, no `message` string to parse.
 * BulkReportComponent posts the report object directly through WebSocketAccessor.
 *
 * Paged because an API Gateway frame is size-capped and a batch's blocked detail can exceed it. The
 * summary rides on every frame, so one surviving frame still carries the counts and the mode. Delivery
 * is best-effort per frame, so pageNumber and totalPages are how a consumer knows the list is partial.
 */
export interface PreflightOperatorReportMessage {
    summary: PreflightSummaryMessage;
    blockedComponents: BlockedComponentDetail[];
    /** One-based, so a first frame is distinguishable from a continuation. */
    pageNumber: number;
    totalPages: number;
}

/** True when a decoded WebSocket payload is a preflight report frame rather than anything else. */
export function isPreflightOperatorReport(payload: any): payload is PreflightOperatorReportMessage {
    return (
        !!payload &&
        !!payload.summary &&
        typeof payload.summary.bulkPromotionId === 'string' &&
        typeof payload.pageNumber === 'number'
    );
}


/**
 * Response to PROMOTE_PREFLIGHT: the accepted batch and how much of it was accepted. The verdict is
 * not in here — it arrives later as a PreflightOperatorReportMessage.
 */
export interface BulkPromoteSubmitResponse {
    batchId: string;
    acceptedCount: number;
}

/** One component's outcome in an executed batch. `wave` is its position in the promotion order. */
export interface ItemOutcome {
    componentId: string;
    disposition: string;
    promotionId?: string;
    reason: string;
    missingDependencies: string[];
    wave: number;
}

/** The five counts a batch is judged by. Skipped is not a failure. */
export interface BulkExecutionCounts {
    requested: number;
    promoted: number;
    blocked: number;
    failed: number;
    skipped: number;
}

/**
 * The result of an executed batch. `configSnapshot` records the settings it ran under, so a later
 * reader is not left inferring them from whatever is configured at the time they look.
 */
export interface BulkExecutionResult {
    batchId: string;
    /**
     * The terminal status the batch reached, when this result was built from the by-id read.
     *
     * Optional because the PROMOTE_EXECUTE acknowledgement does not carry one — it reports that the batch was
     * released to run, not how it ended. Absent on that path, populated on both read-back paths. Without it
     * the outcome screen cannot tell SUCCEEDED from ABANDONED.
     */
    status?: string;
    /** Components that failed during execution. On the by-id read, not on the execute acknowledgement. */
    failedIds?: string[];
    perItem: ItemOutcome[];
    counts: BulkExecutionCounts;
    prerequisites: PrerequisiteEntry[];
    validationMode: string;
    barrierWaitMillis: number;
    configSnapshot: Record<string, string>;
}

/**
 * The PerformBulkActionResponse fields the promote actions populate, carried in the `message` string
 * of a WebSocketMessage envelope.
 *
 * The Smithy names are `preflightResult` and `executionResult`. An earlier client read `preflight` and
 * `execution`, which are not on the wire, so a synchronous verdict was dropped. Both spellings are
 * accepted: the correct one first, the old one so nothing already emitting it regresses.
 *
 * There is no `bulkAction` on the envelope, so a promote message cannot be recognised by that field.
 */
export interface BulkPromoteActionResponse {
    batchId?: string;
    /** Not on PerformBulkActionResponse today; read from preflightResult.acceptedCount when absent. */
    acceptedCount?: number;
    preflightResult?: BulkPreflightResult;
    executionResult?: BulkExecutionResult;
    /** Pre-Smithy spellings, still accepted. */
    preflight?: BulkPreflightResult;
    execution?: BulkExecutionResult;
    phase?: string;
    barrierWaitMillis?: number;
}

/**
 * One row of the bulk history list. Limited to what the list query projects.
 *
 * `onBehalfOf` is gone: it is not a field of the promotion tracker, it is not sent on the request any
 * more, and a column reading it could only ever have shown the promoter's own alias back to them.
 */
export interface BulkPromotionSummary {
    bulkPromotionId: string;
    accountingPeriod: string;
    createdAt: string;
    bulkPromotionStatus: string;
    componentType: string;
    promotedBy: string;
    requestedCount: number;
    promotedCount: number;
    blockedCount: number;
    failedCount: number;
    skippedCount: number;
}

/**
 * Both bulk promotion reads. Must match GetBulkPromotionsResponse in the Smithy model.
 *
 * One operation, GET /promotion/bulk, serving a paginated list or a single batch depending on whether
 * `bulkPromotionId` was supplied. Exactly one of `promotions` and `promotion` is populated per call, so
 * a caller reads the field matching the request it made. BulkPromotionService wraps this as two typed
 * methods, so no component has to make that choice at the call site.
 */
export interface GetBulkPromotionsResponse {
    /** Wire name is `promotions`. Populated on a list read. */
    promotions?: BulkPromotionSummary[];
    /** Absent when there are no further pages. List reads only. */
    nextToken?: string;
    /**
     * One batch in full. Populated on a by-id read. Nested rather than being the body itself; returning
     * the body meant every field read off it was undefined.
     */
    promotion?: BulkPromotionDetail;
}

/** One batch's durable record: the resume path, and the source of the expanded row's ids. */
export interface BulkPromotionDetail extends BulkPromotionSummary {
    promotedIds: string[];
    blocked: BlockedItem[];
    prerequisites: PrerequisiteEntry[];
    validationMode: string;
    phase?: string;
    barrierWaitMillis?: number;
    completedAt?: string;
    /** Smithy spelling of the status, when the response uses it rather than bulkPromotionStatus. */
    status?: string;
    /** Smithy spelling of the id, when the response uses it rather than bulkPromotionId. */
    batchId?: string;
    /** Smithy nests the five counts here rather than carrying them flat. */
    counts?: BulkExecutionCounts;
    /**
     * The configuration the batch was planned under, as the JSON string the service sends. Carries
     * validationMode, which the service does not set on validationMode above — see
     * validationModeFromSnapshot in BulkActionsView.
     */
    configSnapshot?: string;
    /** The ids frozen when the batch was accepted. The only source of what a confirmed batch acts on. */
    submittedIds?: string[];
    failedIds?: string[];
    /** When a parked verdict stops being confirmable and the batch becomes ABANDONED. */
    preflightExpiresAt?: string;
}
